import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSplitById, downloadSplitPDF, resendToSelected, updateAndResend } from '../services/api';
import { SplitDetails } from '../types';
import { useToast } from './Toast';
import SelectiveResendModal from './SelectiveResendModal';
import UpdateSplitModal from './UpdateSplitModal';
import { formatDuration } from '../utils/formatDuration';

const SplitDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [split, setSplit] = useState<SplitDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSelectiveResendModalOpen, setIsSelectiveResendModalOpen] = useState(false);
  const [isSelectiveResending, setIsSelectiveResending] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { showToast, ToastComponent } = useToast();

  useEffect(() => {
    const loadSplitDetails = async () => {
      if (!id) {
        navigate('/my-splits');
        return;
      }
      
      try {
        setLoading(true);
        const data = await getSplitById(id);
        setSplit(data);
        setError(null);
      } catch (err) {
        setError('Failed to load split details');
      } finally {
        setLoading(false);
      }
    };

    loadSplitDetails();
  }, [id, navigate]);

  const getStatusText = (split: SplitDetails, signedCount: number, totalCount: number) => {
    // New status system
    if (split.status === 'confirmed') {
      return 'Confirmed ✓';
    } else if (split.status === 'confirming') {
      if (signedCount === totalCount && totalCount > 0) {
        return 'All Confirmed ✓';
      } else if (signedCount > 0) {
        return `${signedCount}/${totalCount} Confirmed`;
      } else {
        return 'Awaiting Confirmations';
      }
    } else if (split.status === 'unconfirmed') {
      return 'Not Sent';
    }
    
    // Legacy handling
    if (!split.sendInvites) {
      return 'No Invites Sent';
    }
    
    if (signedCount === totalCount && totalCount > 0) {
      return 'All Confirmed ✓';
    } else if (signedCount > 0) {
      return `${signedCount}/${totalCount} Confirmed`;
    } else {
      return 'Pending Confirmations';
    }
  };

  const getStatusBadge = (split: SplitDetails, signedCount: number, totalCount: number) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    // New status system
    if (split.status === 'confirmed') {
      return `${baseClasses} bg-green-500/20 text-green-300 border border-green-500/30`;
    } else if (split.status === 'confirming') {
      if (signedCount === totalCount && totalCount > 0) {
        return `${baseClasses} bg-green-500/20 text-green-300 border border-green-500/30`;
      } else {
        return `${baseClasses} bg-yellow-500/20 text-yellow-300 border border-yellow-500/30`;
      }
    } else if (split.status === 'unconfirmed') {
      return `${baseClasses} bg-accent/20 text-accent border border-accent/30`;
    }
    
    // Legacy handling
    if (!split.sendInvites) {
      return `${baseClasses} bg-accent/20 text-accent border border-accent/30`;
    }
    
    if (signedCount === totalCount && totalCount > 0) {
      return `${baseClasses} bg-green-500/20 text-green-300 border border-green-500/30`;
    } else {
      return `${baseClasses} bg-yellow-500/20 text-yellow-300 border border-yellow-500/30`;
    }
  };

  const handleDownloadPDF = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default button behavior
    
    if (!split?.id) {
      showToast('Split not found', 'error');
      return;
    }
    
    try {
      showToast('Generating PDF...', 'info');
      
      // Use the API to get the PDF download URL
      const pdfUrl = await downloadSplitPDF(split.id);
      
      // Open the S3 signed URL in a new tab to download
      window.open(pdfUrl, '_blank');
      
      showToast('PDF download started!', 'success');
    } catch (error) {
      showToast('Failed to download PDF. Please try again.', 'error');
    }
  };

  // Handle selective resend
  const handleSelectiveResend = async (selectedEmails: string[]) => {
    if (!split?.id) {
      showToast('Split not found', 'error');
      return;
    }

    setIsSelectiveResending(true);

    try {
      const result = await resendToSelected(split.id, selectedEmails);
      
      // Update split state - only reset signatures for selected collaborators
      setSplit(prev => prev ? {
        ...prev,
        status: result.split.status,
        collaborators: prev.collaborators.map(collab => ({
          ...collab,
          signed: selectedEmails.includes(collab.email || '') ? false : collab.signed,
          signedAt: selectedEmails.includes(collab.email || '') ? undefined : collab.signedAt,
          status: selectedEmails.includes(collab.email || '') ? 'pending' as const : collab.status
        }))
      } : null);

      setIsSelectiveResendModalOpen(false);
      showToast(`${result.invites.sent} invite${result.invites.sent !== 1 ? 's' : ''} sent successfully`, 'success');
      
      if (result.invites.failed > 0) {
        showToast(`${result.invites.failed} invite${result.invites.failed !== 1 ? 's' : ''} failed to send`, 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to resend invites. Please try again.', 'error');
    } finally {
      setIsSelectiveResending(false);
    }
  };

  // Handle update split
  const handleUpdateSplit = async (updateData: any) => {
    if (!split?.id) {
      showToast('Split not found', 'error');
      return;
    }

    setIsUpdating(true);

    try {
      const result = await updateAndResend(split.id, updateData);
      
      // Update split state with new data
      setSplit(prev => prev ? {
        ...prev,
        title: result.split.title,
        status: result.split.status,
        collaborators: result.split.collaborators.map(collab => ({
          ...collab,
          status: collab.signed ? 'signed' as const : 'pending' as const,
          signed: collab.signed // Keep for backward compatibility
        }))
      } : null);

      setIsUpdateModalOpen(false);
      showToast(`Split updated! Sent ${result.invites.sent} invite${result.invites.sent !== 1 ? 's' : ''}`, 'success');
      
      if (result.invites.failed > 0) {
        showToast(`${result.invites.failed} invite${result.invites.failed !== 1 ? 's' : ''} failed to send`, 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to update split. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#18181B]">
        <div className="container mx-auto px-4 py-8">
          {ToastComponent}
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#14B8A6]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !split) {
    return (
      <div className="min-h-screen bg-[#18181B]">
        <div className="container mx-auto px-4 py-8">
          {ToastComponent}
          <div className="bg-[#27272A] border border-red-500/40 rounded-2xl p-6">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-400">Error</h3>
                <p className="mt-2 text-[#9CA3AF]">{error || 'Split not found'}</p>
                <Link
                  to="/my-splits"
                  className="mt-4 inline-flex items-center px-4 py-2 bg-[#14B8A6] hover:bg-[#0D9488] text-white rounded-lg transition-colors"
                >
                  Back to My Splits
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalShare = split.collaborators.reduce((sum, collab) => sum + collab.share, 0);
  const signedCount = split.collaborators.filter(collab => collab.status === 'signed').length;
  const totalCount = split.collaborators.length;

  return (
    <div className="min-h-screen bg-[#18181B]">
      <div className="container mx-auto px-4 py-8">
        {ToastComponent}
        
        {/* Header Card */}
        <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-4 sm:p-6 mb-6">
          {/* Mobile Layout */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center min-w-0 flex-1">
                <Link
                  to="/my-splits"
                  className="p-2 -ml-2 hover:bg-[#14B8A6]/20 rounded-lg transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Link>
                <h1 className="text-lg font-bold text-white truncate">{split.title}</h1>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {split.id && (
                  <button
                    onClick={handleDownloadPDF}
                    className="p-2 rounded-lg bg-[#3F3F46] hover:bg-[#52525B] text-white transition-colors"
                    title="Download PDF"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                )}
                {split.isOwner !== false && (
                  <button
                    onClick={() => setIsUpdateModalOpen(true)}
                    className="p-2 rounded-lg bg-[#14B8A6]/20 hover:bg-[#14B8A6]/30 text-[#14B8A6] transition-colors"
                    title="Update & Resend"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 pl-7">
              <span className={getStatusBadge(split, signedCount, totalCount)}>
                {getStatusText(split, signedCount, totalCount)}
              </span>
              <span className="text-xs text-[#6B7280]">
                {new Date(split.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex items-start justify-between gap-4">
            <div className="flex items-center flex-1 min-w-0">
              <Link
                to="/my-splits"
                className="mr-3 p-2 hover:bg-[#14B8A6]/20 rounded-lg transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-white truncate">{split.title}</h1>
                <div className="flex items-center flex-wrap gap-3 mt-1">
                  <span className={getStatusBadge(split, signedCount, totalCount)}>
                    {getStatusText(split, signedCount, totalCount)}
                  </span>
                  <span className="text-xs text-[#6B7280]">
                    {new Date(split.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {split.id && (
                <button
                  onClick={handleDownloadPDF}
                  className="p-2 rounded-lg bg-[#3F3F46] hover:bg-[#52525B] text-white transition-colors"
                  title="Download PDF"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              )}
              {split.isOwner !== false && (
                <button
                  onClick={() => setIsUpdateModalOpen(true)}
                  className="p-2 rounded-lg bg-[#14B8A6]/20 hover:bg-[#14B8A6]/30 text-[#14B8A6] transition-colors"
                  title="Update & Resend"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Collaborators */}
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Collaborators</h2>
              {split.sendInvites && (
                <div className="text-sm text-[#9CA3AF]">
                  {signedCount} of {totalCount} confirmed
                </div>
              )}
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-[#3F3F46]/50">
              {split.collaborators.map((collaborator, index) => (
                <div key={index} className="py-3">
                  {/* Name and Status */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-white">{collaborator.name}</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-[#9CA3AF] capitalize">{collaborator.role}</span>
                        <span className="text-lg font-bold text-[#14B8A6]">{collaborator.share}%</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {collaborator.status === 'signed' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Confirmed
                        </span>
                      ) : collaborator.status === 'expired' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Expired
                        </span>
                      ) : collaborator.status === 'pending' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          Pending
                        </span>
                      ) : (
                        <span className="text-[#6B7280] text-xs">No invite sent</span>
                      )}
                    </div>
                  </div>
                  {/* Secondary info */}
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-[#6B7280]">
                    {collaborator.email && (
                      <span className="text-[#9CA3AF]">{collaborator.email}</span>
                    )}
                    {collaborator.ipiNumber && (
                      <span>IPI: {collaborator.ipiNumber}</span>
                    )}
                    {collaborator.pro && (
                      <span>PRO: {collaborator.pro}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3F3F46]">
                    <th className="text-left py-3 px-2 text-sm font-medium text-[#9CA3AF]">Name</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-[#9CA3AF]">Email</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-[#9CA3AF]">Role</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-[#9CA3AF]">IPI</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-[#9CA3AF]">PRO</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-[#9CA3AF]">Share</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-[#9CA3AF]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {split.collaborators.map((collaborator, index) => (
                    <tr key={index} className="border-b border-[#3F3F46]/50 hover:bg-white/5 transition-colors">
                      <td className="py-3 px-2">
                        <span className="font-medium text-white">{collaborator.name}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-[#9CA3AF] text-sm">{collaborator.email || '-'}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-[#9CA3AF] capitalize">{collaborator.role}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-[#6B7280] text-sm">{collaborator.ipiNumber || '-'}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-[#6B7280] text-sm">{collaborator.pro || '-'}</span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-lg font-bold text-[#14B8A6]">{collaborator.share}%</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {collaborator.status === 'signed' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Confirmed
                          </span>
                        ) : collaborator.status === 'expired' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Expired
                          </span>
                        ) : collaborator.status === 'pending' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Pending
                          </span>
                        ) : (
                          <span className="text-[#6B7280] text-xs">No invite sent</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!split.sendInvites && (
              <div className="mt-4 p-3 bg-accent/10 border border-border-subtle rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-content-muted mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-content-secondary">
                      <strong>No invites sent:</strong> This split was created without sending email invites to collaborators, so confirmation status is not tracked.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Total Validation */}
            <div className="mt-4 pt-4 border-t border-[#3F3F46]">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Total:</span>
                <span className={`text-lg font-bold ${totalShare === 100 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalShare}%
                </span>
              </div>
              {totalShare !== 100 && (
                <p className="text-sm text-red-400 mt-1">
                  ⚠️ Total shares must equal 100%
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          
          {/* Split Info - Redesigned */}
          <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-5">
            <h3 className="text-sm font-medium text-[#6B7280] uppercase tracking-wider mb-4">Details</h3>
            
            {/* Key Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-[#1F1F22] rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-[#14B8A6]">{split.collaborators.length}</div>
                <div className="text-xs text-[#6B7280] mt-1">Collaborators</div>
              </div>
              {split.sendInvites && (
                <div className="bg-[#1F1F22] rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">{signedCount}/{totalCount}</div>
                  <div className="text-xs text-[#6B7280] mt-1">Confirmed</div>
                </div>
              )}
              {!split.sendInvites && (
                <div className="bg-[#1F1F22] rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-[#6B7280]">—</div>
                  <div className="text-xs text-[#6B7280] mt-1">No Invites</div>
                </div>
              )}
            </div>
            
            {/* Info List */}
            <div className="space-y-2.5">
              {split.duration && (
                <div className="flex items-center justify-between py-2 border-b border-[#3F3F46]/50">
                  <span className="text-[#6B7280] text-sm flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Duration
                  </span>
                  <span className="text-white text-sm font-medium">{formatDuration(split.duration)}</span>
                </div>
              )}
              
              {split.creationDate && (
                <div className="flex items-center justify-between py-2 border-b border-[#3F3F46]/50">
                  <span className="text-[#6B7280] text-sm flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Song Date
                  </span>
                  <span className="text-white text-sm font-medium">{new Date(split.creationDate).toLocaleDateString()}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between py-2 border-b border-[#3F3F46]/50">
                <span className="text-[#6B7280] text-sm flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Invites
                </span>
                <span className={`text-sm font-medium ${split.sendInvites ? 'text-green-400' : 'text-[#6B7280]'}`}>
                  {split.sendInvites ? 'Sent' : 'Not Sent'}
                </span>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <span className="text-[#6B7280] text-sm flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Created
                </span>
                <span className="text-white text-sm font-medium">{new Date(split.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            
            {/* Alternate Titles */}
            {split.alternateTitles && split.alternateTitles.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#3F3F46]">
                <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Alternate Titles</div>
                <div className="flex flex-wrap gap-1.5">
                  {split.alternateTitles.map((title, index) => (
                    <span key={index} className="px-2 py-1 bg-[#1F1F22] rounded-md text-xs text-[#9CA3AF]">
                      {title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selective Resend Modal */}
        <SelectiveResendModal
          isOpen={isSelectiveResendModalOpen}
          onClose={() => setIsSelectiveResendModalOpen(false)}
          onConfirm={handleSelectiveResend}
          isLoading={isSelectiveResending}
          collaborators={split.collaborators}
        />

        {/* Update Split Modal */}
        <UpdateSplitModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          onConfirm={handleUpdateSplit}
          isLoading={isUpdating}
          split={split}
        />
        </div>
      </div>
    </div>
  );
};

export default SplitDetailsPage;
